/**
 * Dashboard Assistant Knowledge Base
 * 
 * COMPREHENSIVE GUIDE to every element in the Expert Dashboard/Homepage
 * The assistant uses this to guide users through all dashboard features
 */

// ============================================
// DASHBOARD LAYOUT OVERVIEW
// ============================================

export const DASHBOARD_LAYOUT = {
  overview: `The Expert Dashboard (Homepage) displays key metrics and insights about your advisory practice in SIX main panels arranged in a 3x2 grid:

**TOP ROW (Left to Right):**
1. **Case Status Overview** - Your active cases, progress, and status breakdown
2. **Upcoming Sessions** - Scheduled meetings and reminders
3. **User Journey** - Conversion funnel tracking from widget to concluded advice

**BOTTOM ROW (Left to Right):**
4. **Client Reviews** - Feedback and ratings from your clients
5. **Earnings Summary** - Financial overview and payment tracking
6. **Platform Notifications** - System updates and important alerts

Each panel provides real-time data about different aspects of your advisory business.`,

  navigation: `To navigate the dashboard:
1. All panels display live data that refreshes automatically
2. Click on items within panels for more details
3. Use the AI Assistant (right side) for help understanding any metric
4. Hover over charts for detailed tooltips`
};

// ============================================
// PANEL 1: CASE STATUS OVERVIEW
// ============================================

export const CASE_STATUS_OVERVIEW = {
  name: 'Case Status Overview',
  icon: '📊',
  location: 'Top-left panel',
  description: 'Displays comprehensive statistics about your active cases, their status, and progress.',
  
  sections: {
    statsRow: {
      name: 'Statistics Row',
      location: 'Top of the panel',
      description: 'Key metrics displayed in small cards.',
      metrics: {
        total: {
          label: 'Total',
          description: 'Total number of cases you\'ve handled',
          interpretation: 'This counts all conversations with real clients (not widget tests)'
        },
        active: {
          label: 'Active',
          description: 'Cases currently in progress',
          color: 'Purple',
          interpretation: 'These need your ongoing attention'
        },
        done: {
          label: 'Done',
          description: 'Successfully completed cases',
          color: 'Green',
          interpretation: 'Cases where advice was concluded'
        },
        avgProgress: {
          label: 'Avg %',
          description: 'Average progress percentage across all active cases',
          color: 'Cyan',
          interpretation: 'Higher is better - shows how far along your cases are on average'
        }
      }
    },
    
    pieChart: {
      name: 'Status Breakdown Chart',
      location: 'Left side of the main content area',
      description: 'Visual pie/donut chart showing case distribution.',
      segments: {
        inProgress: {
          color: 'Purple (#7c3aed)',
          description: 'Cases you\'re actively working on'
        },
        completed: {
          color: 'Green (#059669)',
          description: 'Successfully concluded cases'
        },
        started: {
          color: 'Indigo (#4f46e5)',
          description: 'New cases just beginning'
        }
      },
      howToRead: 'Hover over segments to see exact numbers and percentages'
    },
    
    activeCasesList: {
      name: 'Active Cases List',
      location: 'Right side of the main content area',
      description: 'Shows your most recent active cases with progress details.',
      elements: {
        clientName: 'Name of the client',
        progressBar: 'Visual indicator of case completion',
        progressPercent: 'Numerical percentage complete',
        nextAction: 'What needs to be done next (highlighted in amber)',
        lastActivity: 'When you last interacted with this case',
        messageCount: 'Total messages in the conversation'
      },
      howToUse: 'Click on any case to navigate to the Workspace and view full details'
    },
    
    recentActivity: {
      name: 'Recent Activity Footer',
      location: 'Bottom of the panel',
      description: 'Quick stats about recent case activity.',
      metrics: {
        today: 'New cases today',
        week: 'Cases this week',
        month: 'Cases this month'
      }
    }
  },

  commonQuestions: {
    'why is total cases different from expected': 'The total counts only real client conversations. Widget test conversations and internal chats are excluded.',
    'what affects the average progress': 'Progress is calculated based on milestones completed in each case. Cases with defined progress tracking contribute to this metric.',
    'how are cases marked as completed': 'Cases are marked completed when you conclude the advisory process using the conclude button in the Workspace.'
  }
};

// ============================================
// PANEL 2: UPCOMING SESSIONS
// ============================================

export const UPCOMING_SESSIONS = {
  name: 'Upcoming Sessions',
  icon: '📅',
  location: 'Top-center panel',
  description: 'Shows your scheduled meetings and sessions with clients.',
  
  sections: {
    sessionList: {
      name: 'Session List',
      description: 'Chronological list of upcoming appointments.',
      elements: {
        dateTime: 'When the session is scheduled',
        clientName: 'Who you\'re meeting with',
        sessionType: 'Type of meeting (video call, consultation, etc.)',
        status: 'Confirmed, pending, or needs action'
      }
    },
    
    quickActions: {
      name: 'Session Actions',
      description: 'Quick actions for each session.',
      actions: {
        join: 'Join a video call when it\'s time',
        reschedule: 'Change the meeting time',
        cancel: 'Cancel the appointment',
        remind: 'Send a reminder to the client'
      }
    }
  },
  
  howToUse: `To manage your sessions:
1. Review the list to see what's coming up
2. Click on a session for more details
3. Use quick actions to join, reschedule, or manage meetings
4. Sessions sync with your calendar settings`,

  tips: [
    'Check this panel first thing when you log in',
    'Sessions are sorted by time - nearest first',
    'Video call sessions show a "Join" button when it\'s meeting time',
    'You can reschedule directly from this panel'
  ]
};

// ============================================
// PANEL 3: USER JOURNEY (CONVERSION FUNNEL)
// ============================================

export const USER_JOURNEY = {
  name: 'User Journey / Conversion Funnel',
  icon: '🚀',
  location: 'Top-right panel',
  description: 'Tracks user progression from first widget interaction to concluded advice.',
  
  sections: {
    funnelStages: {
      name: 'Funnel Stages',
      description: 'Visual representation of user journey stages.',
      stages: {
        widgetOpened: {
          order: 1,
          color: 'Purple (#8b5cf6)',
          icon: '🚀',
          description: 'Users who opened your widget on your website',
          tracking: 'Tracked when widget is first loaded'
        },
        conversationStarted: {
          order: 2,
          color: 'Indigo (#6366f1)',
          icon: '💬',
          description: 'Users who sent their first message',
          tracking: 'Tracked when first message is sent in widget'
        },
        conversationFinished: {
          order: 3,
          color: 'Blue (#3b82f6)',
          icon: '✓',
          description: 'Users who completed the widget conversation',
          tracking: 'Tracked when conversation reaches conclusion'
        },
        userSignedUp: {
          order: 4,
          color: 'Green (#10b981)',
          icon: '👤',
          description: 'Users who created a platform account',
          tracking: 'Tracked when user registers on the platform'
        },
        meetingBooked: {
          order: 5,
          color: 'Amber (#f59e0b)',
          icon: '📅',
          description: 'Users who scheduled a meeting with you',
          tracking: 'Tracked when meeting is booked through calendar'
        },
        adviceConcluded: {
          order: 6,
          color: 'Dark Green (#059669)',
          icon: '🤝',
          description: 'Users who completed the full advisory process',
          tracking: 'Tracked when you conclude the advice'
        }
      }
    },
    
    conversionRates: {
      name: 'Conversion Rates',
      description: 'Percentage of users moving from one stage to the next.',
      howToRead: 'Each stage shows what % of users progressed from the previous stage',
      interpretation: 'Higher percentages indicate a healthy funnel with good user engagement'
    },
    
    overallConversion: {
      name: 'Overall Conversion',
      location: 'Top-right badge',
      description: 'End-to-end conversion from widget open to concluded advice',
      color: 'Green badge',
      interpretation: 'Your overall success rate in converting widget visitors to paying clients'
    },
    
    weeklyTrend: {
      name: '4-Week Trend Chart',
      location: 'Bottom of the panel',
      description: 'Area chart showing funnel activity over the last 4 weeks',
      dataShown: ['Opened (purple)', 'Started (indigo)', 'Finished (green)'],
      howToRead: 'Look for upward trends indicating growing engagement'
    }
  },
  
  howToUse: `To understand your funnel:
1. Read from top to bottom - this is your user's journey
2. Check where the biggest drop-offs occur
3. The narrower the funnel becomes, the fewer users continue
4. Use insights to improve weak stages
5. Track the weekly trend for patterns`,

  commonQuestions: {
    'why are numbers zero': 'Funnel tracking requires the FunnelSession system to be active. Numbers will populate as users interact with your widget.',
    'what does overall conversion mean': 'It\'s the percentage of users who opened your widget and eventually completed the full advisory process.',
    'how can I improve my funnel': 'Focus on stages with the biggest drop-offs. Common improvements: better widget responses, faster follow-ups, clearer value propositions.'
  }
};

// ============================================
// PANEL 4: CLIENT REVIEWS
// ============================================

export const CLIENT_REVIEWS = {
  name: 'Client Reviews',
  icon: '⭐',
  location: 'Bottom-left panel',
  description: 'Displays feedback, ratings, and reviews from your clients.',
  
  sections: {
    overallStats: {
      name: 'Review Statistics',
      location: 'Top of the panel',
      description: 'Summary metrics about your reviews.',
      metrics: {
        averageRating: {
          description: 'Your overall rating (out of 5 stars)',
          display: 'Large number with star icons'
        },
        totalReviews: {
          description: 'How many reviews you\'ve received'
        },
        fiveStarCount: {
          description: 'Number of 5-star reviews'
        },
        responseRate: {
          description: 'How often you respond to reviews (aim for 100%)'
        }
      }
    },
    
    reviewsList: {
      name: 'Reviews List',
      description: 'Scrollable list of individual client reviews.',
      elements: {
        avatar: 'Client initials in colored circle',
        name: 'Client name (may be abbreviated for privacy)',
        date: 'When the review was submitted',
        rating: 'Star rating (1-5)',
        previewText: 'Short excerpt of the review',
        serviceType: 'What service they received (e.g., "Visa Consultation")',
        readMore: 'Click to see the full review'
      },
      howToUse: 'Click "Read more" on any review to see the full text in a popup'
    },
    
    reviewModal: {
      name: 'Full Review Popup',
      description: 'Detailed view when clicking on a review.',
      contains: [
        'Full review text',
        'Complete rating',
        'Service type',
        '"People found this helpful" count'
      ]
    }
  },
  
  howToUse: `To use the reviews panel:
1. Check your overall rating regularly
2. Scroll through recent reviews
3. Click any review to read the full text
4. Use positive reviews for testimonials
5. Address any concerns in negative reviews`,

  tips: [
    'High ratings attract more clients',
    'Respond to all reviews professionally',
    'Recent reviews appear first',
    'Click reviews to see full client feedback'
  ]
};

// ============================================
// PANEL 5: EARNINGS SUMMARY
// ============================================

export const EARNINGS_SUMMARY = {
  name: 'Earnings Summary',
  icon: '💰',
  location: 'Bottom-center panel',
  description: 'Financial overview showing your earnings and payment information.',
  
  sections: {
    earningsStats: {
      name: 'Earnings Statistics',
      description: 'Key financial metrics.',
      metrics: {
        thisMonth: {
          label: 'This Month',
          description: 'Total earnings for the current month'
        },
        allTime: {
          label: 'All Time',
          description: 'Total earnings since you started'
        },
        pendingPayments: {
          label: 'Pending',
          description: 'Payments that are being processed'
        },
        nextPayout: {
          label: 'Next Payout',
          description: 'Date of your next scheduled payment'
        }
      }
    },
    
    monthlyChart: {
      name: 'Monthly Earnings Chart',
      description: 'Bar chart showing earnings over recent months.',
      howToRead: 'Taller bars indicate higher earnings that month',
      tooltip: 'Hover over bars to see exact amounts'
    },
    
    comparison: {
      name: 'Month-over-Month Comparison',
      description: 'Shows if earnings are up or down from last month',
      display: 'Percentage increase/decrease with arrow indicator'
    }
  },
  
  howToUse: `To understand your earnings:
1. Check "This Month" for current performance
2. Compare with previous months using the chart
3. Track pending payments
4. Note your next payout date`,

  tips: [
    'Pending payments typically process within 7-14 days',
    'Monthly trends help identify seasonal patterns',
    'Higher case volume typically correlates with higher earnings'
  ]
};

// ============================================
// PANEL 6: PLATFORM NOTIFICATIONS
// ============================================

export const PLATFORM_NOTIFICATIONS = {
  name: 'Platform Notifications',
  icon: '🔔',
  location: 'Bottom-right panel',
  description: 'System updates, announcements, and important alerts from the platform.',
  
  sections: {
    notificationList: {
      name: 'Notification List',
      description: 'Scrollable list of notifications.',
      types: {
        feature: {
          icon: 'Laptop/Code icon',
          color: 'Purple/Blue',
          description: 'New feature announcements'
        },
        tip: {
          icon: 'Lightbulb icon',
          color: 'Green',
          description: 'Tips to improve your practice'
        },
        update: {
          icon: 'Update/Sync icon',
          color: 'Amber',
          description: 'Platform updates and maintenance'
        },
        feedback: {
          icon: 'Chat/Feedback icon',
          color: 'Cyan',
          description: 'Requests for your feedback'
        }
      },
      elements: {
        icon: 'Type indicator icon',
        title: 'Notification headline',
        description: 'Brief explanation',
        date: 'When it was posted'
      }
    },
    
    actions: {
      markAsRead: 'Dismiss notification',
      learnMore: 'Get more details about the notification'
    }
  },
  
  howToUse: `To stay informed:
1. Check notifications when you log in
2. Read feature announcements to learn about new tools
3. Act on tips to improve your business
4. Note update schedules for any downtime`,

  tips: [
    'New notifications appear at the top',
    'Feature notifications help you discover new tools',
    'Tip notifications can help improve your practice'
  ]
};

// ============================================
// COMMON DASHBOARD QUESTIONS & ANSWERS
// ============================================

export const COMMON_DASHBOARD_QUESTIONS = {
  // General
  'what is this dashboard': `This is your **Expert Dashboard** - your homepage showing key metrics about your advisory practice.

It includes 6 main panels:
1. **Case Status Overview** - Your active cases and progress
2. **Upcoming Sessions** - Scheduled meetings
3. **User Journey** - Conversion funnel from widget to client
4. **Client Reviews** - Feedback and ratings
5. **Earnings Summary** - Financial overview
6. **Platform Notifications** - System updates

Each panel provides real-time data to help you manage your practice effectively.`,

  'how do I refresh the data': `Dashboard data refreshes automatically every 5 minutes.

To manually refresh:
1. Reload the page (F5 or Cmd+R)
2. Data will update across all panels

Note: Some data like conversion funnel may take a moment to calculate.`,

  // Case Status
  'what do the case numbers mean': `In the **Case Status Overview**:

- **Total**: All cases you've handled
- **Active**: Cases currently in progress (purple)
- **Done**: Successfully completed cases (green)
- **Avg %**: Average progress across active cases

The pie chart shows the same breakdown visually.`,

  'why do I have low case numbers': `Low case numbers could mean:

1. **New account**: You're just getting started
2. **Filtering**: We only count real client conversations
3. **Widget traffic**: Check your widget engagement

To increase cases:
- Promote your widget on your website
- Improve your response time
- Follow up with pending leads`,

  // User Journey
  'what is the conversion funnel': `The **User Journey** panel tracks how users progress from discovering you to becoming clients:

1. 🚀 **Widget Opened** - They found your widget
2. 💬 **Conversation Started** - They engaged with you
3. ✓ **Conversation Finished** - They completed the chat
4. 👤 **User Signed Up** - They created an account
5. 📅 **Meeting Booked** - They scheduled with you
6. 🤝 **Advice Concluded** - Full service delivered

Track where users drop off to improve your process!`,

  'why is my funnel showing zeros': `Zeros in the funnel typically mean:

1. **New tracking**: The funnel tracking system may have just been set up
2. **No widget traffic**: Users haven't interacted with your widget yet
3. **Widget not configured**: Your widget may need the tracking code

As users interact with your widget and progress through the journey, numbers will populate.`,

  // Reviews
  'how do I get more reviews': `To get more client reviews:

1. **Ask satisfied clients** - After concluding advice, request feedback
2. **Make it easy** - Send direct links to leave reviews
3. **Provide great service** - Happy clients review naturally
4. **Follow up** - Remind clients who haven't reviewed yet

High ratings attract more clients to your practice!`,

  // Earnings
  'when do I get paid': `Your earnings are processed based on your payout schedule.

Check the **Earnings Summary** panel for:
- **Pending Payments**: Money being processed
- **Next Payout**: Your next payment date

Payments typically process within 7-14 days after services are completed.`,

  // Sessions
  'how do I manage my calendar': `Your **Upcoming Sessions** panel shows scheduled meetings.

From here you can:
- View upcoming appointments
- Join video calls when it's time
- Reschedule or cancel if needed
- Send reminders to clients

For full calendar management, go to your calendar settings.`,

  // Navigation
  'how do I get to my cases': `To access your cases:

1. Click on any case in the **Case Status Overview** panel, or
2. Navigate to the **Workspace** from the main menu
3. Select a user from the User List

The Workspace gives you full access to case details, conversations, and documents.`
};

// ============================================
// CONTEXTUAL HELP GENERATOR
// ============================================

export const getDashboardContextualHelp = () => {
  return {
    greeting: `Hello! I'm your Dashboard Assistant. I can help you understand any metric or panel on this dashboard. What would you like to know?`,
    suggestions: [
      "What do the numbers mean in Case Status?",
      "How does the conversion funnel work?",
      "How do I get more client reviews?"
    ]
  };
};

// ============================================
// ASSISTANT MODE FOR DASHBOARD
// ============================================

export const DASHBOARD_ASSISTANT_MODE = {
  name: 'Dashboard Assistant',
  icon: '📊',
  description: 'Helps you understand your expert dashboard',
  focusAreas: [
    'case statistics',
    'conversion funnel',
    'client reviews',
    'earnings',
    'sessions',
    'notifications'
  ]
};

// ============================================
// EXPORT DEFAULT
// ============================================

export default {
  DASHBOARD_LAYOUT,
  CASE_STATUS_OVERVIEW,
  UPCOMING_SESSIONS,
  USER_JOURNEY,
  CLIENT_REVIEWS,
  EARNINGS_SUMMARY,
  PLATFORM_NOTIFICATIONS,
  COMMON_DASHBOARD_QUESTIONS,
  getDashboardContextualHelp,
  DASHBOARD_ASSISTANT_MODE
};

